import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Robot, Brain, Target, Warning, Textarea } from '@phosphor-icons/react';

export default function AICoach() {
  const { user, API } = useAuth();
  const [analysis, setAnalysis] = useState(null);
  const [struggles, setStruggles] = useState('');
  const [plan, setPlan] = useState('');
  const [loadingPlan, setLoadingPlan] = useState(false);

  const hasKey = !!user?.ai_api_key;

  useEffect(() => {
    const fetchAnalysis = async () => {
      try {
        const { data } = await axios.post(`${API}/dashboard/analysis`, { force_refresh: false }, { withCredentials: true });
        setAnalysis(data);
      } catch (err) {
        console.error(err);
      }
    };
    fetchAnalysis();
  }, [API]);

  const handlePlanRequest = async (e) => {
    e.preventDefault();
    if (!struggles.trim()) return;
    setLoadingPlan(true);
    try {
      const { data } = await axios.post(`${API}/dashboard/study-coach`, { struggles }, { withCredentials: true });
      setPlan(data.plan_markdown);
    } catch (err) {
      alert("Failed to get advice.");
    } finally {
      setLoadingPlan(false);
    }
  };

  return (
    <div className="container py-8 space-y-8" data-testid="ai-coach-page">
      <div className="flex items-center gap-4">
        <Robot size={48} weight="duotone" className="text-primary" />
        <div>
          <h1 className="text-4xl font-heading text-white uppercase tracking-widest">TACTICAL ADVISOR</h1>
          <p className="text-muted-foreground font-bold tracking-widest text-sm uppercase">AI Performance Analyst & Study Coach</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card className="rounded-none border-border bg-card">
          <CardHeader className="border-b border-border bg-secondary/50">
            <CardTitle className="font-heading text-xl tracking-widest uppercase flex items-center gap-2">
              <Brain className="text-primary" /> PERFORMANCE ANALYSIS
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            {!analysis ? (
              <div className="text-muted-foreground animate-pulse text-sm font-bold tracking-widest uppercase">ANALYZING COMBAT DATA...</div>
            ) : (
              <>
                <div>
                  <h3 className="text-xs font-bold text-chart-5 uppercase tracking-widest mb-3 flex items-center gap-2"><Target /> CONFIRMED STRENGTHS</h3>
                  <ul className="space-y-2">
                    {analysis.strengths?.map((s, i) => (
                      <li key={i} className="text-sm border-l-2 border-chart-5 pl-3 py-1 bg-chart-5/5">{s}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-xs font-bold text-chart-2 uppercase tracking-widest mb-3 flex items-center gap-2"><Warning /> IDENTIFIED VULNERABILITIES</h3>
                  <ul className="space-y-2">
                    {analysis.weaknesses?.map((w, i) => (
                      <li key={i} className="text-sm border-l-2 border-chart-2 pl-3 py-1 bg-chart-2/5">{w}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-xs font-bold text-primary uppercase tracking-widest mb-3 flex items-center gap-2"><Robot /> TACTICAL RECOMMENDATIONS</h3>
                  <ul className="space-y-2">
                    {analysis.recommendations?.map((r, i) => (
                      <li key={i} className="text-sm border-l-2 border-primary pl-3 py-1 bg-primary/5">{r}</li>
                    ))}
                  </ul>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="rounded-none border-border bg-card">
          <CardHeader className="border-b border-border bg-secondary/50">
            <CardTitle className="font-heading text-xl tracking-widest uppercase flex items-center gap-2">
              <Robot className="text-accent" /> DIRECT INTERFACE
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <form onSubmit={handlePlanRequest} className="space-y-4">
              <label className="text-xs font-bold text-muted-foreground uppercase tracking-widest">DESCRIBE CURRENT STRUGGLES</label>
              <textarea
                required
                className="w-full min-h-[120px] bg-black/50 border border-border p-4 text-sm text-white focus:border-primary focus:ring-1 focus:ring-primary outline-none disabled:opacity-50"
                placeholder="E.g. I am struggling with integration calculus and managing time..."
                value={struggles}
                onChange={e => setStruggles(e.target.value)}
                disabled={!hasKey}
                data-testid="coach-input"
              />
              <Button type="submit" disabled={loadingPlan || !hasKey} className="w-full rounded-none font-bold tracking-widest bg-primary hover:bg-primary/80 disabled:opacity-50" data-testid="coach-submit">
                {!hasKey ? 'API KEY REQUIRED' : loadingPlan ? 'COMPUTING STRATEGY...' : 'REQUEST TACTICAL PLAN'}
              </Button>
            </form>

            {plan && (
              <div className="mt-6 p-4 border border-primary/30 bg-primary/5 space-y-4">
                <h4 className="text-xs font-bold text-primary tracking-widest uppercase">TRANSMISSION RECEIVED</h4>
                <div className="prose prose-invert prose-sm max-w-none">
                  {plan.split('\n').map((p, i) => <p key={i}>{p}</p>)}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}